import { useQueryClient } from '@tanstack/react-query';
import { getGetCartQueryKey } from '@unity/commerce-bridge';
import { useCartManagement } from '@unity/commerce-core';
import { useEffect } from 'react';
import { rememberGuestCart } from './guestCart';

/**
 * commerce-core 0.24 stores the guest cart it creates, and the cart a currency
 * sync replaces it with, but its provider keeps the cart id it started with:
 * the next refetch would read a cart that is gone, or create yet another one.
 * This moves the provider and the stored id onto the cart commerce-core
 * holds, seeding that cart's query so it does not blank while it is re-read.
 * Must render inside CartManagementProvider and its QueryClientProvider.
 */
export const useFollowGuestCart = (): void => {
  const { cart, cartId, userId, enableAnonymousCart, setCartId } =
    useCartManagement();
  const queryClient = useQueryClient();
  const heldCartId = cart?.id;

  useEffect(() => {
    if (!enableAnonymousCart || userId || !heldCartId) return;
    rememberGuestCart(heldCartId);
    if (heldCartId === cartId) return;
    const held = queryClient.getQueryData(getGetCartQueryKey(cartId ?? ''));
    queryClient.setQueryData(getGetCartQueryKey(heldCartId), held);
    setCartId(heldCartId);
  }, [enableAnonymousCart, userId, heldCartId, cartId, queryClient, setCartId]);
};
